import { apiClient } from '@/api/axios';
import type { ApiResponse } from '@/types/api';
import type {
  CreateCommentInput,
  CreateRecurringTaskInput,
  CreateRecurringTaskResult,
  CreateTaskInput,
  ListTasksParams,
  PaginatedTasks,
  RecurringTaskRule,
  Task,
  TaskActivityLog,
  TaskAttachment,
  TaskComment,
  TaskDashboardStats,
  TaskTagOption,
  TaskTagsResponse,
  UpdateCommentInput,
  UpdateRecurringTaskInput,
  UpdateTaskAttachmentInput,
  UpdateTaskInput,
} from '@/types/task';
import type { EmployeeSearchResult } from '@/types/user';

const TASKS_BASE = '/api/tasks';

export const taskService = {
  async listTasks(params: ListTasksParams = {}): Promise<PaginatedTasks> {
    const query = Object.fromEntries(
      Object.entries(params).flatMap(([key, value]) => {
        if (value === undefined || value === null || value === '') return [];
        // Booleans must be sent explicitly — axios/query serializers often drop `false`,
        // which breaks Non-Critical (`isCritical=false`) and would omit overdue=false.
        if (typeof value === 'boolean') {
          if (key === 'isCritical' || value === true) {
            return [[key, value ? 'true' : 'false']];
          }
          return [];
        }
        return [[key, value]];
      }),
    );
    const { data } = await apiClient.get<ApiResponse<PaginatedTasks>>(TASKS_BASE, {
      params: query,
    });
    return data.data;
  },

  async createTask(payload: CreateTaskInput): Promise<Task> {
    const { data } = await apiClient.post<ApiResponse<Task>>(TASKS_BASE, payload);
    return data.data;
  },

  async getTask(taskId: string): Promise<Task> {
    const { data } = await apiClient.get<ApiResponse<Task>>(`${TASKS_BASE}/${taskId}`);
    return data.data;
  },

  async updateTask(taskId: string, payload: UpdateTaskInput): Promise<Task> {
    const { data } = await apiClient.put<ApiResponse<Task>>(`${TASKS_BASE}/${taskId}`, payload);
    return data.data;
  },

  async deleteTask(taskId: string): Promise<void> {
    await apiClient.delete(`${TASKS_BASE}/${taskId}`);
  },

  async copyTask(taskId: string, options?: { includeSubtasks?: boolean }): Promise<Task> {
    const { data } = await apiClient.post<ApiResponse<Task>>(`${TASKS_BASE}/${taskId}/copy`, {
      includeSubtasks: options?.includeSubtasks === true,
    });
    return data.data;
  },

  async getDashboardStats(params: ListTasksParams = {}): Promise<TaskDashboardStats> {
    const query = Object.fromEntries(
      Object.entries(params).flatMap(([key, value]) => {
        if (value === undefined || value === null || value === '') return [];
        if (typeof value === 'boolean') {
          if (key === 'isCritical' || value === true) {
            return [[key, value ? 'true' : 'false']];
          }
          return [];
        }
        return [[key, value]];
      }),
    );
    const { data } = await apiClient.get<ApiResponse<TaskDashboardStats>>(
      `${TASKS_BASE}/dashboard`,
      { params: query },
    );
    return data.data;
  },

  async searchTaskAssignees(query: string, limit?: number): Promise<EmployeeSearchResult[]> {
    const trimmed = query.trim();
    const effectiveLimit = limit ?? (trimmed.length < 2 ? 5 : 15);
    const { data } = await apiClient.get<ApiResponse<EmployeeSearchResult[]>>(
      `${TASKS_BASE}/assignees/search`,
      {
        params: { q: trimmed, limit: effectiveLimit },
      },
    );
    return data.data;
  },

  async listTags(): Promise<TaskTagsResponse> {
    const { data } = await apiClient.get<ApiResponse<TaskTagsResponse>>(`${TASKS_BASE}/tags`);
    return data.data;
  },

  async createTag(name: string): Promise<TaskTagOption> {
    const { data } = await apiClient.post<ApiResponse<TaskTagOption>>(`${TASKS_BASE}/tags`, {
      name,
    });
    return data.data;
  },

  async addComment(taskId: string, payload: CreateCommentInput): Promise<TaskComment> {
    const { data } = await apiClient.post<ApiResponse<TaskComment>>(
      `${TASKS_BASE}/${taskId}/comments`,
      payload,
    );
    return data.data;
  },

  async updateComment(
    taskId: string,
    commentId: string,
    payload: UpdateCommentInput,
  ): Promise<TaskComment> {
    const { data } = await apiClient.put<ApiResponse<TaskComment>>(
      `${TASKS_BASE}/${taskId}/comments/${commentId}`,
      payload,
    );
    return data.data;
  },

  async deleteComment(taskId: string, commentId: string): Promise<void> {
    await apiClient.delete(`${TASKS_BASE}/${taskId}/comments/${commentId}`);
  },

  async toggleCommentReaction(
    taskId: string,
    commentId: string,
    emoji: string,
  ): Promise<TaskComment> {
    const { data } = await apiClient.post<ApiResponse<TaskComment>>(
      `${TASKS_BASE}/${taskId}/comments/${commentId}/reactions`,
      { emoji },
    );
    return data.data;
  },

  async getActivityLogs(taskId: string): Promise<TaskActivityLog[]> {
    const { data } = await apiClient.get<ApiResponse<TaskActivityLog[]>>(
      `${TASKS_BASE}/${taskId}/activity`,
    );
    return data.data;
  },

  async listAttachments(taskId: string): Promise<TaskAttachment[]> {
    const { data } = await apiClient.get<ApiResponse<TaskAttachment[]>>(
      `${TASKS_BASE}/${taskId}/attachments`,
    );
    return data.data;
  },

  async uploadAttachment(taskId: string, file: File): Promise<TaskAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<ApiResponse<TaskAttachment>>(
      `${TASKS_BASE}/${taskId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      },
    );
    return data.data;
  },

  async updateAttachment(
    taskId: string,
    attachmentId: string,
    payload: UpdateTaskAttachmentInput,
  ): Promise<TaskAttachment> {
    const { data } = await apiClient.put<ApiResponse<TaskAttachment>>(
      `${TASKS_BASE}/${taskId}/attachments/${attachmentId}`,
      payload,
    );
    return data.data;
  },

  async deleteAttachment(taskId: string, attachmentId: string): Promise<void> {
    await apiClient.delete(`${TASKS_BASE}/${taskId}/attachments/${attachmentId}`);
  },

  async openAttachment(taskId: string, attachmentId: string): Promise<void> {
    const response = await apiClient.get(`${TASKS_BASE}/${taskId}/attachments/${attachmentId}`, {
      responseType: 'blob',
      timeout: 120_000,
    });
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  async downloadAttachment(
    taskId: string,
    attachmentId: string,
    fileName: string,
  ): Promise<void> {
    const response = await apiClient.get(
      `${TASKS_BASE}/${taskId}/attachments/${attachmentId}`,
      {
        params: { download: 'true' },
        responseType: 'blob',
        timeout: 120_000,
      },
    );
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  async createRecurringTask(payload: CreateRecurringTaskInput): Promise<CreateRecurringTaskResult> {
    const { data } = await apiClient.post<ApiResponse<CreateRecurringTaskResult>>(
      `${TASKS_BASE}/recurring`,
      payload,
    );
    return data.data;
  },

  async listRecurringTasks(): Promise<RecurringTaskRule[]> {
    const { data } = await apiClient.get<ApiResponse<RecurringTaskRule[]>>(
      `${TASKS_BASE}/recurring`,
    );
    return data.data;
  },

  async updateRecurringTask(
    ruleId: string,
    payload: UpdateRecurringTaskInput,
  ): Promise<CreateRecurringTaskResult> {
    const { data } = await apiClient.put<ApiResponse<CreateRecurringTaskResult>>(
      `${TASKS_BASE}/recurring/${ruleId}`,
      payload,
    );
    return data.data;
  },

  async deleteRecurringTask(ruleId: string): Promise<void> {
    await apiClient.delete(`${TASKS_BASE}/recurring/${ruleId}`);
  },

  async importFile(file: File): Promise<TaskImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<ApiResponse<TaskImportResult>>(
      `${TASKS_BASE}/import/auto`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      },
    );
    return data.data;
  },

  async importMeetingSheet(file: File): Promise<TaskImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<ApiResponse<TaskImportResult>>(
      `${TASKS_BASE}/import/meeting-sheet`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      },
    );
    return data.data;
  },

  async getImportStatus(importId: string): Promise<TaskImportStatus> {
    const { data } = await apiClient.get<ApiResponse<TaskImportStatus>>(
      `${TASKS_BASE}/import/${importId}`,
    );
    return data.data;
  },

  async listImports(page = 1, limit = 20): Promise<TaskImportListResult> {
    const { data } = await apiClient.get<ApiResponse<TaskImportListResult>>(
      `${TASKS_BASE}/import`,
      { params: { page, limit } },
    );
    return data.data;
  },

  async downloadImportFile(importId: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await apiClient.get(`${TASKS_BASE}/import/${importId}/file`, {
      responseType: 'blob',
      timeout: 120_000,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    let fileName = 'task-import.csv';
    const match = disposition?.match(/filename="?([^";]+)"?/i);
    if (match?.[1]) fileName = match[1];
    return { blob: response.data as Blob, fileName };
  },

  async exportCsv(params: ListTasksParams = {}): Promise<Blob> {
    const query = Object.fromEntries(
      Object.entries(params).flatMap(([key, value]) => {
        if (value === undefined || value === null || value === '') return [];
        if (typeof value === 'boolean') {
          if (key === 'isCritical' || value === true) {
            return [[key, value ? 'true' : 'false']];
          }
          return [];
        }
        return [[key, value]];
      }),
    );
    const { data } = await apiClient.get(`${TASKS_BASE}/export`, {
      params: query,
      responseType: 'blob',
      timeout: 120_000,
    });
    return data as Blob;
  },
};

export interface TaskImportResult {
  importId: string;
  totalRecords: number;
  totalBatches: number;
  status: string;
  insertedRecords: number;
  failedRecords: number;
  errors?: string[];
  message: string;
}

export interface TaskImportStatus {
  importId: string;
  status: string;
  totalRecords: number;
  processedRecords: number;
  insertedRecords: number;
  failedRecords: number;
  totalBatches: number;
  completedBatches: number;
  progressPercent: number;
  errors?: string[] | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface TaskImportHistoryItem {
  id: string;
  table: string;
  originalFileName: string;
  savedFileName: string;
  filePathDisplay: string | null;
  hasFile: boolean;
  uploadedBy: {
    id: string;
    email: string;
    employeeName: string;
    avatarColor?: string;
  };
  totalRecords: number;
  insertedRecords: number;
  failedRecords: number;
  status: string;
  progressPercent: number;
  createdAt: string;
  completedAt?: string | null;
  errors?: string[] | null;
}

export interface TaskImportListResult {
  data: TaskImportHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
